/**
 * Money handling (SRS 0.2 — "store money as integer minor units, never floats").
 *
 * Every amount crossing the API or sitting in the database is an integer number
 * of minor units (paisa/cents). Formatting to a decimal string happens at the
 * edge only. All arithmetic here is integer arithmetic.
 */
export const MINOR_UNITS_PER_MAJOR = 100;
export const MONEY_CAP_MINOR = 10_000_000_000;
export function moneyCapProblem(minor, label) {
    return Math.abs(minor) > MONEY_CAP_MINOR ? `${label} is more than the largest amount the system accepts (${MONEY_CAP_MINOR / MINOR_UNITS_PER_MAJOR})` : null;
}
export function toMinor(major) {
    const value = typeof major === 'string' ? major.trim() : String(major);
    if (!/^-?\d+(\.\d{1,2})?$/.test(value)) {
        throw new Error(`Invalid money value: ${value}`);
    }
    const negative = value.startsWith('-');
    const [whole, fraction = ''] = value.replace('-', '').split('.');
    const minor = Number(whole) * MINOR_UNITS_PER_MAJOR + Number(fraction.padEnd(2, '0'));
    return negative ? -minor : minor;
}
export function toMajorString(minor) {
    const negative = minor < 0;
    const abs = Math.abs(Math.trunc(minor));
    const whole = Math.floor(abs / MINOR_UNITS_PER_MAJOR);
    const frac = abs % MINOR_UNITS_PER_MAJOR;
    return `${negative ? '-' : ''}${whole}.${String(frac).padStart(2, '0')}`;
}
/**
 * The decimal places are pinned, not left to the platform.
 *
 * `Intl` takes the number of fraction digits for a currency from CLDR, and the
 * two runtimes this function has to agree with do not agree with each other:
 * Chrome resolves PKR to 0 digits, Node to 2. So the charge master on screen
 * read "Rs 1,235" for a service priced 1,234.57, while the invoice printed for
 * the same patient — rendered on the server — read "Rs 1,234.57", and a till
 * counted against the screen could never balance.
 *
 * Everything here is stored in hundredths (MINOR_UNITS_PER_MAJOR), so two
 * places is what the number actually holds; showing two is showing the truth.
 */
export function formatMoney(minor, currency = 'PKR', locale = 'en-PK', digits = currency.toUpperCase() === 'COP' ? 0 : 2) {
    const value = Math.trunc(minor) / MINOR_UNITS_PER_MAJOR;
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(value);
    }
    catch {
        return `${currency} ${toMajorString(minor)}`;
    }
}
/** Banker-safe rounding for percentage maths; always returns whole minor units. */
export function roundMinor(value) {
    return Math.round(value + Number.EPSILON);
}
/** Apply a percentage (0-100) to a minor amount, rounding half-up. */
export function applyPercent(minor, percent) {
    return roundMinor((minor * percent) / 100);
}
export function computeLine(params) {
    const { unitPriceMinor, qty, discountPct = 0, taxPct = 0 } = params;
    const whole = params.digits === 0;
    const unit = (value) => (whole ? Math.round(value / MINOR_UNITS_PER_MAJOR) * MINOR_UNITS_PER_MAJOR : value);
    if (!Number.isFinite(qty) || qty <= 0)
        throw new Error('Quantity must be a positive number');
    const gross = unit(roundMinor(unitPriceMinor * qty));
    const tooBig = moneyCapProblem(gross, 'This line');
    if (tooBig)
        throw new Error(tooBig);
    const discount = params.discountMinor !== undefined ? unit(params.discountMinor) : unit(applyPercent(gross, discountPct));
    if (discount < 0 || discount > gross)
        throw new Error('Discount out of range');
    const taxable = gross - discount;
    const tax = unit(applyPercent(taxable, taxPct));
    const net = taxable + tax;
    const netTooBig = moneyCapProblem(net, 'This line');
    if (netTooBig)
        throw new Error(netTooBig);
    return { gross, discount, taxable, tax, net };
}
export function sumLines(lines) {
    return lines.reduce((acc, l) => ({
        gross: acc.gross + l.gross,
        discount: acc.discount + l.discount,
        taxable: acc.taxable + l.taxable,
        tax: acc.tax + l.tax,
        net: acc.net + l.net,
    }), { gross: 0, discount: 0, taxable: 0, tax: 0, net: 0 });
}
//# sourceMappingURL=money.js.map